// src/utils/history.ts
import type { History, Location, To, Path } from '@remix-run/router';
import { createPath, parsePath } from '@remix-run/router';

const isBrowser = () => typeof window !== 'undefined' && typeof document !== 'undefined';

let _history: History | null = null;

// 規格化：去 hash、刪 79、排序
function normalize(path: string): string {
    const hashPos = path.indexOf('#');
    const noHash = hashPos === -1 ? path : path.slice(0, hashPos);
    const qPos = noHash.indexOf('?');
    if (qPos === -1) return noHash;

    const pathname = noHash.slice(0, qPos);
    const rawSearch = noHash.slice(qPos + 1);
    if (!rawSearch) return pathname;

    const params = new URLSearchParams(rawSearch);
    params.delete('79');
    const ordered = Array.from(params.entries()).sort();
    if (ordered.length === 0) return pathname;

    const canonicalSearch = ordered
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
        .join('&');

    return `${pathname}?${canonicalSearch}`;
}

function keyOf(to: To): string {
    return normalize(typeof to === 'string' ? to : createPath(to));
}

// 將 To（string 或 Partial<Path>）補齊為完整 Path
function toFullPath(to: To): Path {
    const p = typeof to === 'string' ? parsePath(to) : (to as Partial<Path>);
    return {
        pathname: p.pathname ?? '/',
        search: p.search ?? '',
        hash: p.hash ?? '',
    };
}

type InitialLocation = Partial<Pick<Location, 'pathname' | 'search' | 'hash'>>;

function createNoopHistory(initialLocation?: InitialLocation): History {
    const noop = () => {};
    const unlisten = () => noop;

    const loc: Location = {
        pathname: initialLocation?.pathname ?? '/',
        search: initialLocation?.search ?? '',
        hash: initialLocation?.hash ?? '',
        state: null,
        key: 'ssr',
    };

    const origin = typeof window === 'undefined' ? 'http://localhost' : window.location.origin;

    const encodeLocation = (to: To): Path => toFullPath(to);
    const createURL = (to: To) => new URL(createPath(encodeLocation(to)), origin);

    type Listener = Parameters<History['listen']>[0];

    return {
        action: 'POP',
        location: loc,
        push: (_to: To, _state?: any) => {},
        replace: (_to: To, _state?: any) => {},
        go: (_n: number) => {},
        back: () => {},
        forward: () => {},
        listen: (_fn: Listener) => unlisten,
        // ✅ 關鍵：SSR 也要回傳穩定 href，不能是空字串
        createHref: (to: To) => {
            const full = encodeLocation(to);
            const path = createPath(full);
            return path && path.length > 0 ? path : '/';
        },
        encodeLocation,
        createURL,
        index: 0,
    } as unknown as History;
}

export function getHistory(initialLocation?: InitialLocation): History {
    if (!isBrowser()) return createNoopHistory(initialLocation);
    if (_history) return _history;

    // 只在瀏覽器載入
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { createBrowserHistory } =
        require('@remix-run/router') as typeof import('@remix-run/router');

    const h: History = createBrowserHistory({ v5Compat: true });

    // 同 URL → 用 replace 取代 push
    const rawPush = h.push.bind(h);
    h.push = (to: To, state?: any) => {
        const currentKey = normalize(window.location.pathname + window.location.search);
        const nextKey = keyOf(to);
        if (currentKey === nextKey) {
            return h.replace(to, state);
        }
        return rawPush(to, state);
    };

    _history = h;
    return _history;
}
